import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Plus, Search, FileText, Trash2, LogOut } from "lucide-react";
import { toast } from "sonner";
import { inr, fmtDT, openWardPdf } from "./wardUtils";
import { DischargeDialog } from "./WardDocs";

const STATUS_TABS = [["admitted", "Admitted"], ["discharged", "Discharged"], ["", "All"]];

export default function WardBills() {
  const [bills, setBills] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("admitted");
  const [discharging, setDischarging] = useState(null);
  const nav = useNavigate();

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/ward/bills", { params: { ...(q ? { q } : {}), ...(status ? { status } : {}) } }); setBills(data); }
    catch { toast.error("Could not load bills"); }
    finally { setLoading(false); }
  };
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [q, status]); // eslint-disable-line

  const remove = async (b) => {
    if (!window.confirm(`Delete bill ${b.bill_no} for ${b.patient_name}?`)) return;
    try { await api.delete(`/ward/bills/${b.id}`); toast.success("Bill deleted"); load(); }
    catch { toast.error("Delete failed"); }
  };

  const outstanding = bills.reduce((s, b) => s + (b.totals?.balance_due || 0), 0);

  return (
    <div className="space-y-6" data-testid="ward-bills-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl font-semibold">Ward Patients</h1>
          <p className="text-sm text-muted-foreground" data-testid="ward-summary">{bills.length} patients · Outstanding {inr(outstanding)}</p>
        </div>
        <Button onClick={() => nav("/ward/bills/new")} data-testid="ward-new-bill" className="rounded-full"><Plus size={16} className="mr-1" /> Admit patient</Button>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="inline-flex rounded-full border border-border p-1" data-testid="ward-status-tabs">
          {STATUS_TABS.map(([k, l]) => (
            <button key={l} onClick={() => setStatus(k)} data-testid={`ward-tab-${l.toLowerCase()}`}
              className={`px-4 py-1.5 rounded-full text-sm transition-colors ${status === k ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-muted"}`}>{l}</button>
          ))}
        </div>
      <div className="relative flex-1 min-w-[220px] max-w-md">
        <Search size={14} className="absolute left-3 top-3 text-muted-foreground" />
        <Input className="pl-9" placeholder="Search by patient name or bill no." value={q} onChange={(e) => setQ(e.target.value)} data-testid="ward-search" />
      </div>
      </div>

      <div className="rounded-xl border border-border overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted/50 text-xs uppercase tracking-wider text-muted-foreground">
            <tr>
              <th className="text-left px-4 py-3">IPD / Bill</th>
              <th className="text-left px-4 py-3">Patient</th>
              <th className="text-left px-4 py-3 hidden md:table-cell">Ward / Bed</th>
              <th className="text-left px-4 py-3 hidden lg:table-cell">Admitted</th>
              <th className="text-left px-4 py-3 hidden sm:table-cell">Status</th>
              <th className="text-right px-4 py-3">Net</th>
              <th className="text-right px-4 py-3">Balance</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={8} className="px-4 py-8 text-center text-muted-foreground">Loading…</td></tr>}
            {!loading && bills.length === 0 && <tr><td colSpan={8} className="px-4 py-10 text-center text-muted-foreground" data-testid="ward-empty">No patients here. Click "Admit patient" to add one.</td></tr>}
            {bills.map((b) => (
              <tr key={b.id} className="border-t border-border hover:bg-muted/30 cursor-pointer" onClick={() => nav(`/ward/bills/${b.id}`)} data-testid={`ward-bill-row-${b.bill_no}`}>
                <td className="px-4 py-3 font-mono text-xs">{b.bill_no}</td>
                <td className="px-4 py-3">
                  <div className="font-medium">{b.patient_name}</div>
                  <div className="text-xs text-muted-foreground">{[b.age, b.sex].filter(Boolean).join(" · ")}</div>
                </td>
                <td className="px-4 py-3 hidden md:table-cell">{b.ward}{b.bed_number ? ` / ${b.bed_number}` : ""}</td>
                <td className="px-4 py-3 hidden lg:table-cell text-muted-foreground">{fmtDT(b.admission_at)}</td>
                <td className="px-4 py-3 hidden sm:table-cell" data-testid={`ward-status-${b.bill_no}`}>
                  {b.discharge_at
                    ? <div><Badge variant="secondary">Discharged{b.discharge_type ? ` · ${b.discharge_type}` : ""}</Badge><div className="text-xs text-muted-foreground mt-1">{fmtDT(b.discharge_at)}</div></div>
                    : <Badge>Admitted</Badge>}
                </td>
                <td className="px-4 py-3 text-right tabular">{inr(b.totals?.net_payable)}</td>
                <td className="px-4 py-3 text-right">
                  <Badge variant={b.totals?.balance_due > 0 ? "destructive" : "secondary"}>{inr(b.totals?.balance_due)}</Badge>
                </td>
                <td className="px-4 py-3 text-right whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                  {!b.discharge_at && <Button variant="ghost" size="icon" onClick={() => setDischarging(b)} data-testid={`ward-discharge-${b.bill_no}`} title="Discharge"><LogOut size={15} /></Button>}
                  <Button variant="ghost" size="icon" onClick={() => openWardPdf(b.id)} data-testid={`ward-pdf-${b.bill_no}`} title="Print bill"><FileText size={15} /></Button>
                  <Button variant="ghost" size="icon" className="text-red-500" onClick={() => remove(b)} data-testid={`ward-delete-${b.bill_no}`} title="Delete"><Trash2 size={15} /></Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {discharging && <DischargeDialog bill={discharging} onClose={() => setDischarging(null)} onDone={() => { setDischarging(null); load(); }} />}
    </div>
  );
}
